import { ConnectedIntegrationChip } from '@modules/integrationsV2/components/ConnectedIntegrationChip';
import { SelectOrAddWorkspaceMenu } from '@modules/integrationsV2/components/SelectOrAddWorkspaceMenu';
import {
  useFetchConnectedIntegrations,
  useIsIntegrationContextEnabled,
  useOnConnectNewIntegration,
} from '@modules/integrationsV2/hooks';
import { useGetConnectedIntegrationWorkspaces } from '@modules/integrationsV2/hooks/useGetConnectedIntegrationWorkspaces';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import {
  getCanAddIntegrationChannelsMetadata,
  getIconForIntegrationType,
  getIsUserOrAccountIntegrationAlreadyConnected,
  trackIntegrationPageEvent,
} from '@modules/integrationsV2/utils';
import { ChevronRightRounded } from '@mui/icons-material';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  IntegrationFormDataViewFragment,
  IntegrationType,
} from 'generated/graphql';
import { capitalize } from 'lodash';
import { useState } from 'react';

const INTEGRATION_TYPE_TO_DESCRIPTION_SUFFIX_MAP: Partial<
  Record<IntegrationType, string>
> = {
  [IntegrationType.Feedback]:
    'to pull customer feedback into Enterpret for analysis.',
  [IntegrationType.User]:
    'to pull user data. User data can be connected with feedback on Enterpret for powerful user-driven analyses.',
  [IntegrationType.Account]:
    'to pull account (company) data. Account data can be connected with feedback on Enterpret for powerful account-driven analyses.',
};

interface SelectNewIntegrationTypeCardProps {
  integrationType: IntegrationType;
  integrationFormData: IntegrationFormDataViewFragment[];
}

export const SelectNewIntegrationTypeCard = ({
  integrationType,
  integrationFormData,
}: SelectNewIntegrationTypeCardProps) => {
  const isIntegrationContextEnabled = useIsIntegrationContextEnabled();

  const selectedIntegrationToConnect = useIntegrationsStore(
    ({ view }) => view.selectedIntegrationToConnect,
  );
  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);

  const [workspaceMenuAnchorEl, setWorkspaceMenuAnchorEl] =
    useState<HTMLDivElement | null>(null);

  const displayName =
    selectedIntegrationToConnect?.integrationChannel.displayName;
  const integrationChannel = selectedIntegrationToConnect?.integrationChannel;

  const integrationFormDataForType = integrationFormData.find(
    (integration) =>
      integration.integrationType === integrationType &&
      integration.channel.type === integrationChannel?.type &&
      integration.channel.displayName === displayName,
  );

  const integrationFormDataToUse =
    integrationFormDataForType ?? integrationFormData[0];

  const oAuthURL =
    integrationFormDataToUse.__typename === 'OAuthFormData'
      ? integrationFormDataToUse.oAuthURL
      : undefined;

  const onConnect = useOnConnectNewIntegration({
    integrationChannel: integrationFormDataToUse.channel,
    oAuthURL,
  });

  const isIntegrationTypeSupported = Boolean(integrationFormDataForType);

  const { connectedIntegrations } = useFetchConnectedIntegrations({
    fetchPolicy: 'cache-first',
  });

  const connectedWorkspaces = useGetConnectedIntegrationWorkspaces({
    integrationType,
    channelType: integrationFormDataToUse.channel.type,
  });

  const isUserOrAccountIntegrationAlreadyConnected =
    getIsUserOrAccountIntegrationAlreadyConnected({
      connectedIntegrations,
      integrationType,
    });

  const renderAction = () => {
    if (!isIntegrationTypeSupported) {
      return <Chip label="Not supported" />;
    }

    if (isUserOrAccountIntegrationAlreadyConnected) {
      return (
        <ConnectedIntegrationChip
          channelType={integrationFormDataToUse.channel.type}
          integrationType={integrationType}
        />
      );
    }

    return null;
  };

  const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      !isIntegrationTypeSupported ||
      isUserOrAccountIntegrationAlreadyConnected
    ) {
      return;
    }

    const canAddIntegrationChannelsMetadata = integrationChannel?.type
      ? getCanAddIntegrationChannelsMetadata(integrationChannel.type)
      : false;

    if (
      isIntegrationContextEnabled &&
      canAddIntegrationChannelsMetadata &&
      connectedWorkspaces.length > 0
    ) {
      setWorkspaceMenuAnchorEl(event.currentTarget);
      return;
    }

    onConnect(integrationType);
    trackIntegrationPageEvent({
      eventName:
        'Integrations - Add Integration Drawer: Clicked Integration Type To Connect',
      integration: {
        integrationType,
        channelType: integrationFormDataToUse.channel.type,
        namespace: undefined,
      },
    });
  };

  const shouldShowConnectIcon =
    isIntegrationTypeSupported && !isUserOrAccountIntegrationAlreadyConnected;

  return (
    <Box position="relative">
      <Card
        variant="outlined"
        sx={{
          height: '100%',
          minWidth: '140px',
          opacity: isIntegrationTypeSupported ? 1 : 0.65,
        }}
      >
        <CardActionArea
          disabled={
            !isIntegrationTypeSupported ||
            isUserOrAccountIntegrationAlreadyConnected
          }
        >
          <CardContent onClick={onClick}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Stack direction="row" spacing={3} maxWidth={750}>
                <Box>
                  {getIconForIntegrationType({
                    integrationType,
                    sx: {
                      fontSize: '40px',
                    },
                  })}
                </Box>
                <Stack spacing={1}>
                  <Typography>
                    {capitalize(integrationType)} Integration
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use {displayName}{' '}
                    {
                      INTEGRATION_TYPE_TO_DESCRIPTION_SUFFIX_MAP[
                        integrationType
                      ]
                    }
                  </Typography>
                </Stack>
              </Stack>
              <Stack width="400px" direction="row" justifyContent="flex-end">
                {shouldShowConnectIcon && (
                  <ChevronRightRounded color="primary" fontSize="large" />
                )}
              </Stack>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          right: '10px',
          transform: 'translateY(-50%)',
        }}
      >
        {renderAction()}
      </Box>
      {integrationChannel && (
        <SelectOrAddWorkspaceMenu
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          oAuthURL={oAuthURL}
          anchorEl={workspaceMenuAnchorEl}
          closeMenu={() => setWorkspaceMenuAnchorEl(null)}
          onSelect={(integrationID) => {
            dispatch({
              type: IntegrationsActionType.SET_VIEW,
              payload: {
                selectedIntegrationIDToUpdate: integrationID,
                isAddDrawerOpen: false,
              },
            });
          }}
          popoverProps={{
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      )}
    </Box>
  );
};
